import { Link, useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import { sidebarStore } from '@/stores/SidebarStore';
import { ZONE2_NAV_ITEMS, isNavActive, SIDEBAR_WIDTH_COLLAPSED, SIDEBAR_WIDTH_EXPANDED } from '@/features/layout/logic';
import { CordeLogo } from '@/components/CordeLogo';

// --- Icon components ---

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
    </svg>
  );
}

function ChatIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function TaskIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
}

function TeamIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87" />
      <path d="M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function ProjectIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function WorkflowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="5" cy="6" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="6" r="2" />
      <circle cx="12" cy="20" r="2" />
      <path d="M7 6h10" />
      <path d="M12 8v2" />
      <path d="M12 14v4" />
    </svg>
  );
}

function BellIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </svg>
  );
}

function GridIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

function MyWorkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}

function TemplateIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  );
}

function ActivityIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

const ICON_MAP: Record<string, React.FC<{ className?: string }>> = {
  inbox: InboxIcon,
  activity: ActivityIcon,
  mywork: MyWorkIcon,
  chat: ChatIcon,
  task: TaskIcon,
  document: DocumentIcon,
  calendar: CalendarIcon,
  team: TeamIcon,
  project: ProjectIcon,
  tag: TagIcon,
  template: TemplateIcon,
  workflow: WorkflowIcon,
};

interface SidebarProps {
  onSearchOpen: () => void;
  onQuickCreate: () => void;
  onAppLauncher: () => void;
  onTenantSwitcher: () => void;
  notificationCount: number;
  userInitial: string;
}

export const Sidebar = observer(({
  onSearchOpen,
  onQuickCreate,
  onAppLauncher,
  onTenantSwitcher,
  notificationCount,
  userInitial,
}: SidebarProps) => {
  const { t } = useI18n();
  const location = useLocation();
  const expanded = sidebarStore.isExpanded;
  const width = expanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  return (
    <nav
      aria-label={t('layout.sidebar.label')}
      className="hidden md:flex flex-col h-full bg-sidebar border-r border-sidebar-border z-10 transition-[width] duration-200"
      style={{ width }}
    >
      {/* Zone 1: Navigation (fixed top) */}
      <div className={`flex flex-col items-center gap-1 px-2 pt-3 pb-2 border-b border-sidebar-border ${!expanded ? '[&>*]:justify-center' : ''}`}>
        {/* Logo → Home */}
        <Link
          to="/inbox"
          className="flex items-center justify-center w-full h-10 rounded-md hover:bg-sidebar-accent transition-colors"
          aria-label={t('layout.sidebar.home')}
        >
          <CordeLogo className="h-6 w-6 text-primary flex-shrink-0" />
          {expanded && (
            <span className="ml-3 text-sm font-semibold text-sidebar-foreground truncate flex-1">
              Corde
            </span>
          )}
        </Link>

        {/* Search */}
        <button
          onClick={onSearchOpen}
          className="flex items-center w-full h-9 px-2 rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          aria-label={t('layout.shortcuts.search')}
        >
          <SearchIcon className="h-4 w-4 flex-shrink-0" />
          {expanded && (
            <>
              <span className="ml-3 text-sm truncate flex-1 text-left">{t('layout.sidebar.search')}</span>
              <kbd className="ml-auto text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">⌘K</kbd>
            </>
          )}
        </button>

        {/* Quick Create */}
        <button
          onClick={onQuickCreate}
          className="flex items-center w-full h-9 px-2 rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          aria-label={t('layout.sidebar.quickCreate')}
        >
          <PlusIcon className="h-4 w-4 flex-shrink-0" />
          {expanded && (
            <span className="ml-3 text-sm truncate flex-1 text-left">{t('layout.sidebar.quickCreate')}</span>
          )}
        </button>
      </div>

      {/* Zone 2: Ambient Tools (scrollable) */}
      <div className={`flex-1 overflow-y-auto scrollbar-slim px-2 py-2 space-y-0.5 ${!expanded ? '[&>*]:justify-center' : ''}`}>
        {ZONE2_NAV_ITEMS.map((item) => {
          const active = isNavActive(location.pathname, item.href);
          const Icon = ICON_MAP[item.icon];
          return (
            <Link
              key={item.id}
              to={item.href}
              className={`
                flex items-center h-9 px-2 rounded-md text-sm font-medium transition-colors
                ${active
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
                }
              `}
              aria-current={active ? 'page' : undefined}
            >
              {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
              {expanded && (
                <span className="ml-3 truncate flex-1">{t(item.labelKey)}</span>
              )}
              {item.badgeCount !== undefined && item.badgeCount > 0 && (
                <Badge variant="primary" size="sm" className="ml-auto">
                  {item.badgeCount > 99 ? '99+' : item.badgeCount}
                </Badge>
              )}
            </Link>
          );
        })}
      </div>

      {/* Zone 3: Products (scrollable) */}
      <div className={`px-2 py-2 border-t border-sidebar-border ${!expanded ? '[&>*]:justify-center' : ''}`}>
        {sidebarStore.pins.map((pin) => (
          <Link
            key={pin.id}
            to={pin.href}
            className="flex items-center h-9 px-2 rounded-md text-sm text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          >
            <span className="h-4 w-4 flex-shrink-0 flex items-center justify-center text-xs font-bold">
              {pin.name.charAt(0).toUpperCase()}
            </span>
            {expanded && <span className="ml-3 truncate flex-1">{pin.name}</span>}
          </Link>
        ))}
        {/* App Launcher */}
        <button
          onClick={onAppLauncher}
          className="flex items-center w-full h-9 px-2 rounded-md text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          aria-label={t('layout.sidebar.appLauncher')}
        >
          <GridIcon className="h-4 w-4 flex-shrink-0" />
          {expanded && <span className="ml-3 text-sm truncate flex-1 text-left">{t('layout.sidebar.appLauncher')}</span>}
        </button>
      </div>

      {/* Zone 4: System (fixed bottom) */}
      <div className={`flex flex-col gap-0.5 px-2 py-2 border-t border-sidebar-border ${!expanded ? '[&>*]:justify-center' : ''}`}>
        {/* Notifications */}
        <Link
          to="/notifications"
          className="relative flex items-center w-full h-9 px-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          aria-label={t('layout.sidebar.notifications')}
        >
          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
            <BellIcon className="h-4 w-4" />
          </span>
          {notificationCount > 0 && (
            <span className="absolute top-1 left-5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-medium px-1" aria-live="polite">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
          {expanded && <span className="ml-2 text-sm truncate flex-1">{t('layout.sidebar.notifications')}</span>}
        </Link>

        {/* Settings */}
        <Link
          to="/settings"
          className="flex items-center w-full h-9 px-2 rounded-md text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          aria-label={t('layout.sidebar.settings')}
        >
          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
            <SettingsIcon className="h-4 w-4" />
          </span>
          {expanded && <span className="ml-2 text-sm truncate flex-1">{t('layout.sidebar.settings')}</span>}
        </Link>

        {/* Collapse toggle (desktop only) */}
        <button
          onClick={() => sidebarStore.toggleCollapse()}
          className="flex items-center w-full h-9 px-2 rounded-md text-left text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          aria-label={expanded ? t('layout.sidebar.collapse') : t('layout.sidebar.expand')}
        >
          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center">
            {expanded ? <ChevronLeftIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          </span>
          {expanded && <span className="ml-2 text-sm truncate flex-1">{t('layout.sidebar.collapse')}</span>}
        </button>

        {/* Avatar / Tenant Switcher */}
        <button
          onClick={onTenantSwitcher}
          className="flex items-center w-full h-9 px-2 rounded-md text-left text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors"
          aria-label={t('layout.sidebar.profile')}
        >
          <span className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-medium text-primary flex-shrink-0">
            {userInitial}
          </span>
          {expanded && <span className="ml-2 text-sm truncate flex-1">{t('layout.sidebar.profile')}</span>}
        </button>
      </div>
    </nav>
  );
});
